import { useMemo, useCallback, useEffect, useState, useRef, type ReactNode } from 'react';

import { MasterDocument } from '../MasterDocument';
import { DocumentDynamicEntities } from '../DocumentDynamicEntities';
import {
  useDocumentDynamicEntities,
  type DocumentDynamicEntitiesHandle,
} from './useDocumentDynamicEntities';
import { CurrencyPanel } from '../CurrencyPanel';
import { CurrencySelectorModal } from '../CurrencySelectorModal';
import { DocumentModalList, ConfirmDialog } from '../DocumentModalList';
import { CreateFromSourceModal } from '../CreateFromSourceModal';
import type { CurrencyInfo, IMasterDocumentAdapter } from '../adapter/MasterDocumentAdapter.types';
import type { DocumentModalListConfig } from '../template/MasterDocumentTemplate.types';
import {
  useMasterDocumentAdapter,
  useMasterDocumentTemplate,
  useIconRegistry,
  useUserPermissions,
  useFactBoxWidgets,
  useLineTabRenderers,
} from '../provider/MasterDocumentProvider';
import {
  MasterDocumentRefreshProvider,
  type MasterDocumentRefreshApi,
} from '../provider/MasterDocumentRefreshProvider';
import { useMasterDocumentPrint } from '../printing/MasterDocumentPrintProvider';
import { useMasterDocumentAudit } from '../audit/MasterDocumentAuditProvider';
import type { AuditContext } from '../audit/MasterDocumentAuditProvider';
import type { PrintContext } from '../printing/MasterDocumentPrinting.types';
import { PrintIcon, HistoryIcon } from '../icons';
import { useMasterDocumentReducer } from './useMasterDocumentReducer';
import { toast } from '../../Toast';
import { useFactBoxData } from './useFactBoxData';
import { useCrossing } from './useCrossing';
import { createCrossingFetch } from './crossingFetch';
import { buildFactBoxSections } from './factBoxBuilder';
import { isLineFieldVisibleInState } from './lineFieldVisibility';

import type {
  TransitionConfig,
  ActionConfig,
  ContextStripItem,
  ComputedRule,
  LookupFieldConfig,
  RowAction,
  DocumentLineData,
  LineTabConfig,
  LineTabExternalFilter,
} from '../MasterDocument.types';
import type { SerializableTransitionConfig, SerializableActionConfig, ActionRegistryEntry, LineModalWidgetConfig, LineActionEntry, DetailTabFlexQuery } from '../template/MasterDocumentTemplate.types';
import { LineActionFormModal } from '../LineActionFormModal';
import { evaluateLineActionGate, runLineAction } from './lineActionGating';
import { useMasterDocumentTranslation } from '../i18n';
import { operacionDeLineaPermitida } from '../lineStateAxis';
import { classifyError } from './userFacingError';
import { createCurrencyFormatter, useMasterDocumentFormatters } from '../formatters';
import { resolveActionDisabledState } from '../actionVisibility';
import { evaluateCondition } from '../fieldCondition';
import type { FieldCondition } from '../fieldCondition';
import { evaluateExpression } from '../template/expressionEvaluator';

// ════════════════════════════════════════════════════════════
// PERMISSIONS HELPER — Filtra campos según permisos del usuario
// ════════════════════════════════════════════════════════════

import type { FieldDescriptor } from '../MasterDocument.types';

/**
 * Aplica RBAC declarativo sobre un array de campos.
 *
 * - `permissions === null` → sistema no activo → devuelve campos sin cambios.
 * - `permissionVisibility` ausente → campo siempre visible.
 * - `permissionEnable` ausente → editabilidad controlada solo por editableOn/docState.
 */
function applyPermissions(
  fields: FieldDescriptor[],
  permissions: string[] | null,
): FieldDescriptor[] {
  if (permissions === null) return fields;

  return fields
    .filter((f) => !f.permissionVisibility || permissions.includes(f.permissionVisibility))
    .map((f) => {
      if (!f.permissionEnable || permissions.includes(f.permissionEnable)) return f;
      return { ...f, editableOn: [] };
    });
}

// ════════════════════════════════════════════════════════════
// LINE WIDGET PANEL — Sidebar panel que se carga vía adapter
// ════════════════════════════════════════════════════════════

function LineWidgetPanel({
  config,
  formValues,
  documentId,
  adapter,
}: {
  config: LineModalWidgetConfig;
  formValues: Record<string, unknown>;
  documentId: string | undefined;
  adapter: IMasterDocumentAdapter;
}) {
  const { t } = useMasterDocumentTranslation();
  const [data, setData] = useState<Record<string, unknown>>({});
  const [loading, setLoading] = useState(false);
  const generationRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Build a snapshot of only the watched fields to avoid unnecessary fetches
  const watchSnapshot = useMemo(() => {
    const snap: Record<string, unknown> = {};
    config.watchFields.forEach((k) => { snap[k] = formValues[k]; });
    return snap;
  }, [config.watchFields, formValues]);

  useEffect(() => {
    if (!adapter.getLineWidgetData) return;

    if (timerRef.current) clearTimeout(timerRef.current);

    const delay = config.debounceMs ?? 300;
    timerRef.current = setTimeout(async () => {
      const gen = ++generationRef.current;
      setLoading(true);
      try {
        const result = await adapter.getLineWidgetData!(config.widgetKey, watchSnapshot, documentId);
        if (generationRef.current === gen) setData(result);
      } catch {
        // Leave previous data on error
      } finally {
        if (generationRef.current === gen) setLoading(false);
      }
    }, delay);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [watchSnapshot]); // eslint-disable-line react-hooks/exhaustive-deps

  const entries = Object.entries(data);

  return (
    <div className="flex flex-col h-full">
      {/* Panel header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-secondary dark:border-dark-border-primary shrink-0">
        <span className="text-xs font-semibold uppercase tracking-wide text-content-secondary dark:text-dark-content-secondary">
          {config.title}
        </span>
        {loading && (
          <svg className="w-3.5 h-3.5 animate-spin text-primary-custom-500 dark:text-dark-border-custom" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
            <path className="opacity-80" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
          </svg>
        )}
      </div>

      {/* KV list */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2.5">
        {entries.length === 0 && !loading && (
          <p className="text-xs text-content-secondary dark:text-dark-content-secondary italic">
            {t('master_document.chrome.noData')}
          </p>
        )}
        {entries.map(([key, value]) => (
          <div key={key} className="flex flex-col gap-0.5">
            <span className="text-[10px] font-medium uppercase tracking-wide text-content-secondary dark:text-dark-content-secondary">
              {key}
            </span>
            <span className="text-sm font-medium text-content-primary dark:text-dark-content-primary break-words">
              {String(value ?? '—')}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// PROPS — Solo documentId. Todo lo demás viene del template
//         y del adapter via MasterDocumentProvider.
// ════════════════════════════════════════════════════════════

/**
 * Motivo por el que el kit pide navegar a otro documento que ya existe en el backend.
 *
 * Los dos orígenes piden destinos distintos, y por eso el motivo viaja al host:
 * - `'traceability'`: nodo del grafo de trazabilidad → normalmente solo lectura.
 * - `'generated'`: documento recién generado por un cruce → normalmente edición.
 */
export type DocumentNavigationReason = 'traceability' | 'generated';

/** Contexto entregado a `onNavigateToDocument`. */
export interface DocumentNavigationContext {
  reason: DocumentNavigationReason;
}

export interface SmartMasterDocumentProps {
  /**
   * UUID (v7) del documento a cargar. Es la PK de document_headers.id.
   * Si no se pasa, entra en modo 'create'.
   *
   * NO es el document_number legible ("PV-2026-004821"), sino el UUID
   * globalmente único que identifica el registro sin ambigüedad,
   * independiente de compañía, centro de operación o tipo de documento.
   */
  documentId?: string;

  /**
   * Estado inicial pre-poblado por el backend via GET /documents/new.
   * Cuando se provee, el documento arranca en modo 'create' con los
   * headerValues ya resueltos (compañía, CO, tipo). No hay ID todavía;
   * el POST se hace al guardar por primera vez.
   */
  initialState?: import('../adapter/MasterDocumentAdapter.types').DocumentState;

  /** Llamado tras un guardado exitoso. Usado por MasterDocumentPage para volver al listado. */
  onSaved?: () => void;

  /**
   * Llamado cuando una acción elimina el documento y devuelve
   * `{ success: true, navigateToList: true }`. Usado por MasterDocumentPage
   * para volver al listado tras borrar/anular el documento desde el detalle.
   */
  onNavigateToList?: () => void;

  /**
   * Fuerza el modo 'view' (solo lectura) independiente de lo que retorne el adapter.
   * Usado por "Ver detalle" en el listado.
   */
  viewOnly?: boolean;

  /**
   * Adapter alternativo. Si se pasa, toma precedencia sobre el del contexto.
   * Usado por MasterDocumentPage para inyectar un adapter con navegación interceptada.
   */
  adapterOverride?: IMasterDocumentAdapter;

  /**
   * Navegación a OTRO documento que ya existe en el backend, pedida desde el detalle:
   * un nodo del grafo de trazabilidad, o el documento recién generado por un cruce.
   *
   * ⚠️ Sin este prop el kit emite el pseudo-key `navigate_document:<id>` por el canal
   * de `adapter.executeAction` — comportamiento histórico, que solo
   * `MasterDocumentPage` intercepta. Un host que renderiza este componente en su
   * propia ruta DEBE pasarlo: de lo contrario el pseudo-key llega a su `executeAction`
   * real, que no lo conoce, y el clic termina en un error visible o en nada.
   *
   * @see DocumentNavigationReason — para distinguir el origen y elegir el destino
   */
  onNavigateToDocument?: (documentId: string, context: DocumentNavigationContext) => void;
}

// ════════════════════════════════════════════════════════════
// CURRENCY PANEL — renderizado desde template.currency
// ════════════════════════════════════════════════════════════


// ════════════════════════════════════════════════════════════
// COMPONENT
// ════════════════════════════════════════════════════════════

/**
 * SmartMasterDocument — componente inteligente que conecta el template JSON
 * con el adaptador de datos para renderizar un documento maestro completo.
 *
 * Orquesta el ciclo de vida del documento:
 * - Carga y mutación de estado vía `useMasterDocumentReducer`
 * - Carga asíncrona de datos del FactBox vía `useFactBoxData`
 * - Conflicto de concurrencia duro derivado del 409 del servidor (`state.conflict`)
 * - Construcción de transiciones, acciones y secciones del FactBox
 *   a partir del template declarativo
 *
 * @see MasterDocumentProvider — proveedor que inyecta el template y el adaptador
 * @see MasterDocument — componente de presentación pura que recibe los datos
 *
 * @example
 * ```tsx
 * <MasterDocumentProvider template={invoiceTemplate} adapter={myAdapter}>
 *   <SmartMasterDocument documentId="FA-001" />
 * </MasterDocumentProvider>
 * ```
 */
export function SmartMasterDocument({ documentId, initialState, onSaved, viewOnly, adapterOverride, onNavigateToList, onNavigateToDocument }: SmartMasterDocumentProps) {
  const contextAdapter = useMasterDocumentAdapter();
  const adapter = adapterOverride ?? contextAdapter;
  const template = useMasterDocumentTemplate();
  const iconRegistry = useIconRegistry();
  const userPermissions = useUserPermissions();
  const factBoxWidgets = useFactBoxWidgets();
  const lineTabRenderers = useLineTabRenderers();
  const print = useMasterDocumentPrint();
  const audit = useMasterDocumentAudit();
  const { t } = useMasterDocumentTranslation();
  const { locale } = useMasterDocumentFormatters();

  // ── Campos adicionales (EAV): segunda fase del guardado ──
  //
  // Indirección por ref a propósito. El reducer necesita los callbacks para
  // invocarlos DESPUÉS de persistir, pero el hook que los produce necesita
  // `state.documentClass`, que sale del propio reducer. Un ref rompe el ciclo sin
  // duplicar estado: los callbacks son estables y leen el valor vigente.
  const dynamicEntitiesRef = useRef<DocumentDynamicEntitiesHandle | null>(null);

  const persistDynamicHeader = useCallback(async (persistedDocumentId: string) => {
    await dynamicEntitiesRef.current?.header?.save(persistedDocumentId);
  }, []);

  const persistDynamicLine = useCallback(async (persistedLineId: string) => {
    await dynamicEntitiesRef.current?.line?.save(persistedLineId);
  }, []);

  const persistenceHooks = useMemo(
    () => ({ onHeaderPersisted: persistDynamicHeader, onLinePersisted: persistDynamicLine }),
    [persistDynamicHeader, persistDynamicLine],
  );

  const {
    state,
    dispatch,
    handleHeaderFieldChange,
    handleLineSave,
    handleDeleteLine,
    handleDuplicateLine,
    handleTransition,
    handleAction,
  } = useMasterDocumentReducer(
    template,
    adapter,
    documentId,
    initialState,
    onSaved,
    viewOnly,
    onNavigateToList,
    persistenceHooks,
  );

  const dynamicEntities = useDocumentDynamicEntities({
    config: template.dynamicEntities,
    documentClass: state.documentClass ?? template.identity.documentClass,
  });

  // El ref se sincroniza en un efecto y no en el render: los callbacks de arriba
  // solo corren desde un evento del usuario, siempre después de pintar.
  useEffect(() => {
    dynamicEntitiesRef.current = dynamicEntities;
  }, [dynamicEntities]);

  // ── Error toast ───────────────────────────────────
  useEffect(() => {
    if (!state.error) return;
    toast.error(t('master_document.chrome.error'), { description: state.error });
    dispatch({ type: 'SET_ERROR', error: undefined });
  }, [state.error, t]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── FactBox data loading ──────────────────────────
  useFactBoxData(
    template.factBox.sections,
    adapter,
    state,
    dispatch,
  );

  // ── Conflict detection ────────────────────────────
  // La fuente de verdad de un conflicto es el 409 del servidor
  // (`OPTIMISTIC_LOCK_VIOLATION`) en cualquier mutación, persistido por el reducer
  // en `state.conflict` con `hard: true`. Se eliminó el polling blando
  // (`useConflictDetection`): llegaba segundos tarde y era descartable, permitiendo
  // guardar con un token viejo. Ahora el conflicto es duro y no descartable.

  // ── Clase del documento abierto ──────────────────────────
  // `template.identity.documentClass` es una constante POR SUPERFICIE, así que no
  // sirve cuando una superficie sirve a varias clases (p.ej. notas crédito de
  // venta y de solo valor comparten visor). La clase del estado —puesta por el
  // wizard vía `initialState`, o devuelta por el adapter en `getDocument`— manda
  // sobre ella. Ausente ⇒ la del template, que es el caso de todos los documentos
  // de una sola clase.
  const resolvedDocClass = state.documentClass ?? template.identity.documentClass;

  // ── Dynamic document states (from API) ───────────────────
  // GET /api/v1/documents/{documentClass}/states
  // Allows adding/modifying states in the DB without touching the template.
  // Falls back to template.stateMachine.states when the endpoint is unavailable.
  const [dynamicStates, setDynamicStates] = useState<import('../adapter/MasterDocumentAdapter.types').DocumentStatesResponse | null>(null);

  useEffect(() => {
    if (!adapter.getDocumentStates) return;
    let cancelled = false;
    adapter.getDocumentStates(resolvedDocClass)
      .then((s) => { if (!cancelled) setDynamicStates(s); })
      .catch(() => { if (!cancelled) setDynamicStates(null); });
    return () => { cancelled = true; };
  }, [adapter, resolvedDocClass]);

  // ── Available actions (from DocumentProcess endpoint) ────
  // GET /api/v1/documents/{id}/available-actions?state={docState}
  // Returns the keys of actions enabled for the current user/state/role.
  // When null (method not implemented), falls back to template's visibleOn.
  const [availableActionKeys, setAvailableActionKeys] = useState<string[] | null>(null);

  useEffect(() => {
    if (!adapter.getAvailableActions) {
      setAvailableActionKeys(null);
      return;
    }
    const docId = state.documentId ?? '';
    let cancelled = false;
    adapter.getAvailableActions(docId, state.docState)
      .then((keys) => { if (!cancelled) setAvailableActionKeys(keys); })
      .catch(() => { if (!cancelled) setAvailableActionKeys(null); });
    return () => { cancelled = true; };
  }, [adapter, state.documentId, state.docState]);

  // ── Resolve icon string → ReactNode ───────────────
  const resolveIcon = useCallback(
    (iconName?: string): ReactNode => {
      if (!iconName) return undefined;
      const Icon = iconRegistry[iconName];
      if (!Icon) return undefined;
      return <Icon className="w-4 h-4" />;
    },
    [iconRegistry],
  );

  // ── Acciones de línea ADICIONALES (declarativas) ──────────
  // Espejo, a nivel de línea, del flujo de `handleAction`, PERO solo para las
  // acciones EXTRA declaradas en `template.lineActionRegistry`. Las acciones de
  // línea quemadas (Editar/Ver/Duplicar/Cerrar-reabrir/Eliminar) NO pasan por aquí.
  // Qué acción de la ActionBar está en vuelo (P1 de LOADING-INTEGRATION.md).
  // `state.saving` dice que HAY una mutación, no CUÁL: con él solo se puede
  // deshabilitar toda la barra, que es lo que ya se hacía. Este estado local
  // añade la identidad para que gire el botón que el usuario pulsó.
  //
  // Vive aquí y no en el reducer a propósito: es presentación, y el reducer es
  // contrato público que consumen los hosts.
  //
  // El prefijo desambigua: una transición se identifica por su destino y una
  // acción por su clave, y nada impide que coincidan.
  const [runningAction, setRunningAction] = useState<string | null>(null);

  const runTransition = useCallback(async (to: string) => {
    setRunningAction(`t:${to}`);
    try {
      await handleTransition(to);
    } finally {
      setRunningAction(null);
    }
  }, [handleTransition]);

  const runAction = useCallback(async (key: string) => {
    setRunningAction(`a:${key}`);
    try {
      await handleAction(key);
    } finally {
      setRunningAction(null);
    }
  }, [handleAction]);

  // Resincroniza el documento completo desde el backend (P8 de
  // LOADING-INTEGRATION.md).
  //
  // Los tres sitios que llaman aquí REEMPLAZAN el documento entero y lo hacían
  // de forma invisible: `getDocument` sin `LOAD_START`, así que el usuario veía
  // los datos viejos hasta que cambiaban de golpe. Con el despacho, el mismo
  // `SkeletonDocument` de la carga inicial cubre la espera. No lleva `Loading`
  // a propósito: para una vista completa la máscara de layout es mejor que un
  // spinner, y ya existe.
  //
  // El `LOAD_ERROR` NO es opcional: sin él un refetch fallido deja el skeleton
  // girando para siempre. Antes ese fallo era inocuo porque no había skeleton
  // que colgar — de hecho uno de los tres sitios ni siquiera tenía `.catch`, y
  // otro lo tragaba en silencio.
  // Señal de "las líneas cambiaron" para los listados que NO son la grilla nativa
  // (pestañas Tipo 2 / Tipo 3). El host no puede observar `state.lines` —vive en
  // su propio fetch—, así que se le entrega un contador que sube en cada mutación
  // del kit y que él usa como dependencia. Es declarativo a propósito: la
  // alternativa era guardar un `refetch` imperativo del componente del host, con
  // el riesgo de quedarse con una referencia muerta al desmontar la pestaña.
  const [refreshToken, setRefreshToken] = useState(0);
  const bumpRefresh = useCallback(() => setRefreshToken((n) => n + 1), []);

  const refetchDocument = useCallback(async () => {
    if (!state.documentId || !adapter.getDocument) return;
    dispatch({ type: 'LOAD_START' });
    try {
      const fresh = await adapter.getDocument(state.documentId);
      dispatch({ type: 'LOAD_SUCCESS', payload: fresh });
      // Cubre lo que no pasa por las tres mutaciones de línea: acciones de fila,
      // recarga por conflicto y anexado desde origen.
      bumpRefresh();
    } catch (err) {
      // Mismo tratamiento que la carga inicial: el texto técnico va al log y el
      // saneado a `state.error`, que ya dispara el toast.
      const { message, technical } = classifyError(err, t);
      if (technical) console.error('[MasterDocument] resincronización falló:', technical);
      dispatch({ type: 'LOAD_ERROR', payload: message });
    }
  }, [state.documentId, adapter, dispatch, t, bumpRefresh]);

  // Lo que el host ve de la resincronización: recargar el documento y saber que
  // algo cambió. Se publica al subárbol entero (widgets del FactBox, renderers de
  // pestañas, paneles propios) porque hasta ahora un componente del host que
  // persistía por su cuenta no tenía forma de avisarle al kit — y el kit solo
  // mergea lo que guarda él, así que la pantalla se quedaba con los valores
  // previos hasta un F5.
  const refreshApi = useMemo<MasterDocumentRefreshApi>(
    () => ({ reload: refetchDocument, refreshToken }),
    [refetchDocument, refreshToken],
  );

  // Qué línea está mutando (P4 de LOADING-INTEGRATION.md). Mismo patrón que
  // `runningAction`: `state.saving` dice que HAY una mutación, no sobre qué fila.
  //
  // Se rastrea aquí y NO en el reducer —que era lo que el análisis preveía—
  // porque las tres operaciones ya reciben el `lineId` como primer argumento:
  // no hace falta tocar el contrato que consumen los hosts.
  const [busyLineId, setBusyLineId] = useState<string | null>(null);

  const runLineSave = useCallback(async (lineId: string | null, data: Record<string, unknown>) => {
    // `lineId === null` es una línea nueva: todavía no hay fila que marcar.
    setBusyLineId(lineId);
    try {
      const errors = await handleLineSave(lineId, data);
      // Solo cuenta como mutación si el guardado pasó: un rechazo de validación
      // no cambió las líneas y no debe hacer refetch a los listados del host.
      if (!errors || Object.keys(errors).length === 0) bumpRefresh();
      return errors;
    } finally {
      setBusyLineId(null);
    }
  }, [handleLineSave, bumpRefresh]);

  // Línea pendiente de confirmar el borrado (null = sin diálogo abierto). Antes el borrado se
  // confirmaba con un `window.confirm` dentro del reducer: un cuadro del navegador, ajeno al estilo
  // del kit, que bloquea el hilo y no se puede probar sin parchear `window`. Ahora la pregunta la
  // hace el `ConfirmDialog` del kit, igual que `DocumentList` para las acciones de fila.
  const [pendingDeleteLineId, setPendingDeleteLineId] = useState<string | null>(null);

  const runDeleteLine = useCallback((lineId: string) => {
    setPendingDeleteLineId(lineId);
  }, []);

  const confirmDeleteLine = useCallback(async () => {
    const lineId = pendingDeleteLineId;
    if (!lineId) return;
    setPendingDeleteLineId(null);
    setBusyLineId(lineId);
    try {
      await handleDeleteLine(lineId);
      bumpRefresh();
    } finally {
      setBusyLineId(null);
    }
  }, [pendingDeleteLineId, handleDeleteLine, bumpRefresh]);

  const runDuplicateLine = useCallback(async (lineId: string) => {
    setBusyLineId(lineId);
    try {
      await handleDuplicateLine(lineId);
      bumpRefresh();
    } finally {
      setBusyLineId(null);
    }
  }, [handleDuplicateLine, bumpRefresh]);

  const [activeLineAction, setActiveLineAction] = useState<{ key: string; entry: LineActionEntry } | null>(null);
  const [lineActionLineId, setLineActionLineId] = useState<string>('');
  const [lineActionBusy, setLineActionBusy] = useState(false);
  const [lineActionError, setLineActionError] = useState<string | undefined>(undefined);

  // Ejecuta una acción de línea adicional (vía override `executeLineAction` o vía
  // genérica `endpoint`+`genericFetch`) y aplica el outcome: merge de
  // `updatedData`, o refetch del documento cuando la vía genérica no devuelve
  // datos frescos, + toast. La decisión vive en `runLineAction` (testeable).
  const handleLineAction = useCallback(
    async (
      actionKey: string,
      lineId: string,
      payload: Record<string, unknown>,
      entry: LineActionEntry,
    ) => {
      setLineActionBusy(true);
      setLineActionError(undefined);
      try {
        const outcome = await runLineAction(adapter, state, entry, actionKey, lineId, payload);
        if (!outcome.ok) {
          // `runLineAction` es puro y no traduce: si el mensaje lo generó el kit
          // propaga `errorKey` y lo resolvemos aquí al idioma activo. Si vino del
          // backend, `error` ya llega traducido (el kit envía `Accept-Language`).
          const errorText = outcome.errorKey ? t(outcome.errorKey) : outcome.error;
          // `custom` no abre modal del kit: el error se muestra vía toast. El
          // `formModal` lo muestra dentro del propio modal (setLineActionError).
          if (entry.actionType === 'custom') {
            if (errorText) toast.error(errorText);
          } else {
            setLineActionError(errorText);
          }
          return;
        }
        // Resincronización: `merge` (preciso) o `refetch` (recarga del documento
        // cuando la vía genérica no devuelve datos frescos). Ver runLineAction.
        if (outcome.merge) {
          dispatch({ type: 'MERGE_STATE', payload: outcome.merge });
        } else if (outcome.refetch) {
          // `refetchDocument` no relanza: un fallo de resincronización ya no se
          // confunde con un fallo de la acción, que a estas alturas YA tuvo
          // éxito. Antes caía en el `catch` de abajo y el usuario leía "la
          // acción falló" sobre una acción que sí se aplicó.
          await refetchDocument();
        }
        if (outcome.message) toast.success(outcome.message);
        setActiveLineAction(null);
        setLineActionLineId('');
      } catch (err) {
        const { message, technical } = classifyError(err, t);
        if (technical) console.error('[MasterDocument] acción de línea falló:', technical);
        if (entry.actionType === 'custom') {
          toast.error(message);
        } else {
          setLineActionError(message);
        }
      } finally {
        setLineActionBusy(false);
      }
    },
    [adapter, state, dispatch, t, refetchDocument],
  );

  // Construye las acciones de línea EXTRA para una fila, gateadas por D2. Las
  // acciones quemadas se anexan en `LinesSection` (no aquí). Sin
  // `lineActionRegistry` ⇒ no hay acciones extra (retrocompatible).
  const getLineActions = useCallback(
    (line: DocumentLineData): RowAction[] => {
      const registry = template.lineActionRegistry;
      if (!registry) return [];
      const actions: RowAction[] = [];
      for (const [key, entry] of Object.entries(registry)) {
        const gate = evaluateLineActionGate(entry, line, state.docState, userPermissions);
        if (!gate.visible) continue;
        actions.push({
          key,
          label: entry.label,
          icon: resolveIcon(entry.icon),
          variant: entry.variant,
          disabled: gate.disabled,
          onClick: () => {
            if (entry.actionType === 'formModal') {
              setLineActionError(undefined);
              setActiveLineAction({ key, entry });
              setLineActionLineId(line.id);
            } else if (entry.actionType === 'custom') {
              // El kit NO abre su modal: delega en `adapter.executeLineAction`
              // con payload vacío. El consumidor aporta su propia UX (p.ej. un
              // modal con picker) dentro del override. El merge/refetch/toast del
              // `ActionResult` los aplica `handleLineAction` igual que el formModal.
              void handleLineAction(key, line.id, {}, entry);
            }
          },
        });
      }
      return actions;
    },
    [template.lineActionRegistry, state.docState, userPermissions, resolveIcon, handleLineAction],
  );

  // ── Map serializable transitions → TransitionConfig[]
  // API transitions take priority; fall back to template-embedded transitions.
  const transitions = useMemo<TransitionConfig[]>(() => {
    // API transitions: use when getDocumentStates returned them
    if (dynamicStates?.transitions && dynamicStates.transitions.length > 0) {
      const seen = new Set<string>();
      return dynamicStates.transitions
        .filter((t) => t.from === state.docState)
        .filter((t) => {
          // Deduplicate by actionKey — show only the first transition per action.
          if (seen.has(t.actionKey)) return false;
          seen.add(t.actionKey);
          return true;
        })
        .map((t) => {
          // Look up action metadata (label, icon, style):
          // 1. Inline in transition (backend-provided) → 2. actionRegistry (v1.1) → 3. actions[] (v1.0) → 4. raw key
          const registryEntry: ActionRegistryEntry | undefined = template.actionRegistry?.[t.actionKey];
          const legacyMeta = registryEntry ? undefined : template.actions?.find((a: SerializableActionConfig) => a.key === t.actionKey);
          const label = t.label ?? registryEntry?.label ?? legacyMeta?.label ?? t.actionKey;
          const icon = resolveIcon(t.icon ?? registryEntry?.icon ?? legacyMeta?.icon);
          const style = (t.variant ?? registryEntry?.variant ?? legacyMeta?.variant ?? 'secondary') as import('../MasterDocument.types').ActionVariant;
          // Gating in-flight (Caso B): mientras hay una mutación en curso, las
          // transiciones (aprobar/anular) se deshabilitan para evitar doble disparo.
          return {
            to: t.to,
            actionKey: t.actionKey,
            label,
            icon,
            style,
            hasGuard: t.hasGuard,
            disabled: state.saving,
            loading: runningAction === `t:${t.to}`,
          };
        });
    }
    // Template fallback (v1.0 format with embedded transitions)
    const templateTransitions = template.stateMachine.transitions ?? [];
    return templateTransitions
      .filter((t: SerializableTransitionConfig) => t.from.includes(state.docState))
      .map((t: SerializableTransitionConfig) => ({
        to: t.to,
        label: t.label,
        icon: resolveIcon(t.icon),
        style: t.style,
        hasGuard: t.hasGuard,
        shortcut: t.shortcut,
        disabled: state.saving,
        loading: runningAction === `t:${t.to}`,
      }));
  }, [template.stateMachine.transitions, template.actions, template.actionRegistry, dynamicStates, state.docState, state.saving, resolveIcon, runningAction]);

  // ── Map serializable actions → ActionConfig[] ─────
  // Priority: API (availableActionKeys) > template visibleOn
  // When the API returns a list, it acts as the whitelist — only those keys are shown.
  // When null (endpoint not implemented), falls back to template's visibleOn logic.
  const actions = useMemo<ActionConfig[]>(() => {
    // Keys for the current state that are already rendered as transition buttons.
    // These must NOT also appear as action buttons (would create visible duplicates).
    const activeTransitionKeys = new Set(
      (dynamicStates?.transitions ?? [])
        .filter((t) => t.from === state.docState)
        .map((t) => t.actionKey),
    );

    // v1.1: use actionRegistry only when the API provides availableActionKeys.
    // Without that whitelist every registry entry would render as a button,
    // including transition-label entries that are only used for key→label resolution.
    if (template.actionRegistry && availableActionKeys !== null) {
      return Object.entries(template.actionRegistry)
        .filter(([key, entry]) => {
          if (activeTransitionKeys.has(key)) return false;
          if (entry.permissionVisibility && userPermissions !== null && !userPermissions.includes(entry.permissionVisibility)) return false;
          // Una acción con `disabledStates` que incluya el estado actual se
          // muestra (deshabilitada) aunque no venga en availableActionKeys.
          const { disabled } = resolveActionDisabledState(entry, state.docState);
          return availableActionKeys.includes(key) || (entry.alwaysVisible && !!state.documentId) || disabled;
        })
        .map(([key, entry]: [string, ActionRegistryEntry]) => {
          const { disabled, disabledReason } = resolveActionDisabledState(entry, state.docState);
          return {
            key,
            label: entry.label,
            icon: resolveIcon(entry.icon),
            variant: entry.variant,
            // Una acción deshabilitada no ejecuta nada (ni abre confirmación).
            onClick: disabled
              ? () => {}
              : entry.actionType === 'modalList' && entry.modalList
                ? () => {
                    setActiveModalListConfig(entry.modalList!);
                    setModalListLineId(undefined);
                    setModalListOpen(true);
                  }
                : entry.actionType === 'createFromSource'
                  ? () => setCreateFromSourceOpen(true)
                  : () => { void runAction(key); },
            // Gating in-flight (Caso B): además de su estado declarativo, la acción
            // se deshabilita mientras haya una mutación en curso.
            disabled: disabled || state.saving,
            // Solo la pulsada gira; las demás siguen solo deshabilitadas. Las que
            // abren un modal (modalList / createFromSource) son síncronas y nunca
            // llegan aquí en vuelo.
            loading: runningAction === `a:${key}`,
            disabledReason,
            position: entry.position,
            requiresConfirmation: disabled ? false : (entry.actionType === 'modalList' ? false : entry.requiresConfirmation),
            confirmationMessage: disabled ? undefined : (entry.actionType === 'modalList' ? undefined : entry.confirmationMessage),
          };
        });
    }

    // v1.0 fallback: actions array with visibleOn
    return (template.actions ?? [])
      .filter((a: SerializableActionConfig) => {
        if (a.permissionVisibility && userPermissions !== null && !userPermissions.includes(a.permissionVisibility)) return false;
        if (availableActionKeys !== null) {
          return availableActionKeys.includes(a.key) && !activeTransitionKeys.has(a.key);
        }
        if (a.visibleOn && a.visibleOn.length > 0) {
          return a.visibleOn.includes(state.docState) || a.visibleOn.includes('*');
        }
        return true;
      })
      .map((a: SerializableActionConfig) => {
        // Even in v1.0 mode, check if this key has a modalList config in actionRegistry
        const registryEntry = template.actionRegistry?.[a.key];
        const isModalList = registryEntry?.actionType === 'modalList' && !!registryEntry.modalList;
        const isCreateFromSource = registryEntry?.actionType === 'createFromSource';
        return {
          key: a.key,
          label: a.label,
          icon: resolveIcon(a.icon),
          variant: a.variant,
          onClick: isModalList
            ? () => {
                setActiveModalListConfig(registryEntry!.modalList!);
                setModalListLineId(undefined);
                setModalListOpen(true);
              }
            : isCreateFromSource
              ? () => setCreateFromSourceOpen(true)
              : () => { void runAction(a.key); },
          disabled: (a.disabledOn?.includes(state.docState) ?? false) || state.saving,
          loading: runningAction === `a:${a.key}`,
          position: a.position,
        };
      });
  }, [template.actions, template.actionRegistry, state.docState, state.saving, resolveIcon, handleAction, runAction, runningAction, availableActionKeys, dynamicStates, userPermissions]);

  // ── Impresión nativa (botón en la barra del detalle) ──
  // Acción de presentación: NO pasa por adapter.executeAction ni por el
  // actionRegistry. Se anexa al array de acciones cuando la impresión está
  // disponible para el documento guardado (regla §4.3-a).
  const actionsWithPrint = useMemo<ActionConfig[]>(() => {
    if (!print) return actions;
    const ctx: PrintContext = {
      source: 'detail',
      documentId: state.documentId ?? '',
      documentNumber: (state.headerValues['_documentNumber'] as string | undefined) ?? null,
      docState: state.docState || null,
    };
    if (!print.isPrintAvailable(ctx)) return actions;
    return [
      ...actions,
      {
        key: 'print',
        label: print.label,
        icon: <PrintIcon className="w-4 h-4" />,
        // 'ghost' por defecto → texto primary-600 legible (SPEC §5.2), no compite
        // con la primaria. Configurable vía printing.buttonVariant.
        variant: print.buttonVariant,
        onClick: () => print.openPrint(ctx),
      },
    ];
  }, [print, actions, state.documentId, state.headerValues, state.docState]);

  // ── Historial de auditoría (acción en la barra del detalle) ──
  // Acción de presentación (no pasa por adapter/actionRegistry). Se anexa cuando la
  // auditoría está disponible (enabled + documento guardado). Se encadena sobre
  // actionsWithPrint para no perder el botón de impresión.
  const actionsWithAudit = useMemo<ActionConfig[]>(() => {
    if (!audit) return actionsWithPrint;
    const ctx: AuditContext = {
      source: 'detail',
      documentId: state.documentId ?? '',
    };
    if (!audit.isAuditAvailable(ctx)) return actionsWithPrint;
    return [
      ...actionsWithPrint,
      {
        key: 'audit-history',
        label: audit.label,
        icon: <HistoryIcon className="w-4 h-4" />,
        variant: 'ghost',
        onClick: () => audit.openAudit(ctx),
      },
    ];
  }, [audit, actionsWithPrint, state.documentId]);

  // ── Context strip items ───────────────────────────
  // La COMPAÑÍA sube a la cabecera, como badge bajo el título (canon del detalle
  // de MasterCrud), así que se excluye de la franja: dejarla en los dos sitios la
  // pintaría dos veces en la misma pantalla. `_companyName` es la clave de la
  // convención — la declaran 19 de las 43 plantillas de core; donde no está, la
  // cabecera simplemente no pinta el badge y la franja no pierde nada.
  const COMPANY_FIELD = '_companyName';

  const contextItems = useMemo<ContextStripItem[]>(() => {
    return template.contextStrip
      .filter((m) => m.field !== COMPANY_FIELD)
      .map((m) => ({
        label: m.label,
        value: String(state.headerValues[m.field] ?? ''),
        mono: m.mono,
        accent: m.accent,
      }));
  }, [template.contextStrip, state.headerValues]);

  const companyName = useMemo(() => {
    const declarada = template.contextStrip.some((m) => m.field === COMPANY_FIELD);
    if (!declarada) return undefined;
    const valor = String(state.headerValues[COMPANY_FIELD] ?? '').trim();
    return valor || undefined;
  }, [template.contextStrip, state.headerValues]);

  // ── Currency code (resolved from headerValues or defaultCode) ──
  const currencyCode = useMemo(() => {
    const cfg = template.currency;
    if (!cfg) return '';
    return String(state.headerValues[cfg.codeField] ?? '') || cfg.defaultCode || '';
  }, [template.currency, state.headerValues]);

  // ── Fetch exchange rate from backend ──────────────
  const [exchangeRate, setExchangeRate] = useState<{ rate: number; label: string } | null>(null);

  useEffect(() => {
    if (!currencyCode || !adapter.getExchangeRate) {
      setExchangeRate(null);
      return;
    }
    let cancelled = false;
    adapter.getExchangeRate(currencyCode)
      .then((result) => { if (!cancelled) setExchangeRate(result); })
      .catch(() => { if (!cancelled) setExchangeRate(null); });
    return () => { cancelled = true; };
  }, [currencyCode, adapter]);

  // ── DocumentModalList state ───────────────────────
  const [modalListOpen, setModalListOpen] = useState(false);
  const [activeModalListConfig, setActiveModalListConfig] = useState<DocumentModalListConfig | null>(null);
  const [modalListLineId, setModalListLineId] = useState<string | undefined>(undefined);

  // ── CreateFromSource state (modo append, caso B) ──
  const [createFromSourceOpen, setCreateFromSourceOpen] = useState(false);

  // ── Currency selector modal ───────────────────────
  const [currencyModalOpen, setCurrencyModalOpen] = useState(false);
  const [availableCurrencies, setAvailableCurrencies] = useState<CurrencyInfo[]>([]);
  const [currenciesLoading, setCurrenciesLoading] = useState(false);

  const handleOpenCurrencyModal = useCallback(() => {
    setCurrencyModalOpen(true);
    if (availableCurrencies.length === 0 && adapter.listCurrencies) {
      setCurrenciesLoading(true);
      adapter.listCurrencies(true)
        .then((list) => setAvailableCurrencies(list))
        .catch(() => {})
        .finally(() => setCurrenciesLoading(false));
    }
  }, [adapter, availableCurrencies.length]);

  const handleCurrencySelect = useCallback(
    (currency: CurrencyInfo) => {
      setCurrencyModalOpen(false);
      // Actualización optimista: el panel cambia de inmediato
      const cfg = template.currency;
      if (cfg) handleHeaderFieldChange(cfg.codeField, currency.code);
      // Persiste en backend en background (si el adapter lo soporta)
      adapter.changeCurrency?.(state.documentId ?? '', currency.code);
    },
    [adapter, state.documentId, template.currency, handleHeaderFieldChange],
  );

  // ── Currency panel ────────────────────────────────
  const currencyPanel = useMemo<ReactNode>(() => {
    const cfg = template.currency;
    if (!cfg || !currencyCode) return undefined;

    const rate = exchangeRate?.rate ?? (cfg.rateField ? state.headerValues[cfg.rateField] : undefined);
    const rateLabel = exchangeRate?.label ?? cfg.rateLabel;
    const canEdit = cfg.editable && state.mode !== 'view';

    return (
      <CurrencyPanel
        code={currencyCode}
        rate={rate as string | number | undefined}
        rateLabel={rateLabel}
        onClick={canEdit ? handleOpenCurrencyModal : undefined}
      />
    );
  }, [template.currency, currencyCode, exchangeRate, state.headerValues, state.mode, handleOpenCurrencyModal]);

  // ── Computed rules → bridge to function-based ─────
  const lineComputedRules = useMemo<ComputedRule[]>(() => {
    return template.computedRules
      .filter((r) => {
        const field = template.fields.find((f) => f.key === r.targetField);
        return field?.context === 'line';
      })
      .map((r) => ({
        targetField: r.targetField,
        watch: r.watch,
        compute: (formData: Record<string, unknown>) =>
          evaluateExpression(r.expression, formData, adapter.evaluateCustomRule?.bind(adapter)),
      }));
  }, [template.computedRules, template.fields, adapter]);

  // ── Adapter con formatCurrency ajustado a la moneda activa ──
  // Usa el MISMO formateador que la grilla de líneas (`createCurrencyFormatter`):
  // el widget de Totales y la grilla leen los mismos números y no pueden
  // discrepar en los decimales.
  const factBoxAdapter = useMemo(() => {
    if (!currencyCode) return adapter;
    try {
      const fmt = createCurrencyFormatter(locale, currencyCode);
      return { ...adapter, formatCurrency: fmt };
    } catch {
      return adapter;
    }
  }, [adapter, currencyCode, locale]);

  // ── FactBox sections → ReactNode children ─────────
  const factBoxSections = useMemo(
    () => buildFactBoxSections(template.factBox.sections, state, factBoxAdapter, userPermissions, factBoxWidgets, t, refreshApi),
    [template.factBox.sections, state, factBoxAdapter, userPermissions, factBoxWidgets, t, refreshApi],
  );

  // ── Conflict config ───────────────────────────────
  // Único origen: el 409 del servidor (`state.conflict`, `hard: true`). El modal
  // duro no es descartable: la única acción es "Recargar documento", que descarta
  // la edición en vuelo, re-obtiene el documento + row_version y reconcilia.
  const conflictProp = useMemo(() => {
    const active = state.conflict;
    if (!active) return undefined;
    const clear = () => dispatch({ type: 'CLEAR_CONFLICT' });
    return {
      modifiedBy: active.modifiedBy,
      modifiedAgo: active.modifiedAgo,
      message: active.message,
      // hard ⇒ no descartable (sin "seguir con mis cambios"); blando ⇒ descartable.
      dismissable: !active.hard,
      onReload: () => {
        clear();
        void refetchDocument();
      },
      // En el conflicto duro no se permite seguir editando con el token viejo.
      onKeepEditing: active.hard ? undefined : clear,
    };
  }, [state.conflict, dispatch, refetchDocument]);

  // ── Forbidden / 403 banner ────────────────────────
  const forbiddenProp = useMemo(() => {
    if (!state.forbidden) return undefined;
    return {
      message: state.forbidden.message,
      onDismiss: () => dispatch({ type: 'CLEAR_FORBIDDEN' }),
    };
  }, [state.forbidden, dispatch]);

  // Resolved badge: API overrides template for the current state
  const resolvedStateBadge = useMemo(() => {
    if (dynamicStates) {
      const entry = dynamicStates.states.find((s) => s.key === state.docState);
      if (entry) return { label: entry.label, bg: entry.bg, color: entry.color };
    }
    // Fallback to template (old record format)
    return template.stateMachine.states?.[state.docState];
  }, [dynamicStates, state.docState, template.stateMachine.states]);

  // ── Field rules (from API) ────────────────────────────────
  // GET /api/v1/documents/{documentClass}/field-rules?state={docState}
  // Overrides editableOn/visibleOn from the template for the current state.
  // When null (method not implemented), falls back to template field arrays.
  const [fieldRules, setFieldRules] = useState<Record<string, { editable: boolean; visible: boolean }> | null>(null);

  useEffect(() => {
    if (!adapter.getFieldRules) {
      setFieldRules(null);
      return;
    }
    let cancelled = false;
    adapter.getFieldRules(resolvedDocClass, state.docState)
      .then((rules) => { if (!cancelled) setFieldRules(rules); })
      .catch(() => { if (!cancelled) setFieldRules(null); });
    return () => { cancelled = true; };
  }, [adapter, resolvedDocClass, state.docState]);

  // ── Dynamic select options (optionsSource) ───────────────
  // For fields with optionsSource, fetch options from API once per companyId/coId change.
  // Stored as Record<fieldKey, SelectOption[]> — merged into the field's options array.
  const [dynamicOptions, setDynamicOptions] = useState<Record<string, Array<{ label: string; value: string }>>>({});

  useEffect(() => {
    if (!adapter.getSelectOptions) return;

    const companyId = String(state.headerValues._companyId ?? '');
    const coId = String(state.headerValues._coId ?? '');

    const fieldsWithSource = template.fields.filter(
      (f) => f.context === 'header' || f.context === 'line' || f.context === 'line-detail',
    ).filter((f) => (f as import('../MasterDocument.types').FieldDescriptor & { optionsSource?: string }).optionsSource);

    if (fieldsWithSource.length === 0) return;

    let cancelled = false;
    const loads = fieldsWithSource.map(async (f) => {
      const src = (f as { optionsSource?: string }).optionsSource!;
      try {
        const opts = await adapter.getSelectOptions!(src, companyId || undefined, coId || undefined);
        if (!cancelled) setDynamicOptions((prev) => ({ ...prev, [f.key]: opts }));
      } catch { /* fallback: keep static options */ }
    });
    Promise.allSettled(loads);

    return () => { cancelled = true; };
  }, [adapter, template.fields, state.headerValues._companyId, state.headerValues._coId]);

  // ── Separate header vs line fields ────────────────
  // Applies template visibleOn filtering + visibleWhen/hiddenWhen conditions
  // + API field-rules overrides + dynamic options.
  const headerFields = useMemo(() => {
    const base = template.fields.filter((f) => {
      if (f.context !== 'header') return false;
      if (f.visibleOn && f.visibleOn.length > 0 && !f.visibleOn.includes('*')) {
        if (!f.visibleOn.includes(state.docState)) return false;
      }
      // Field-level conditional visibility, evaluated against the current header
      // values — parity with resolvedHeaderSections / resolvedLineColumns. Without
      // this, a header field's visibleWhen/hiddenWhen was silently ignored and the
      // field always rendered (e.g. exchange-rate always shown in local currency).
      if (f.visibleWhen && !evaluateCondition(f.visibleWhen, state.headerValues as Record<string, unknown>)) return false;
      if (f.hiddenWhen && evaluateCondition(f.hiddenWhen, state.headerValues as Record<string, unknown>)) return false;
      return true;
    });
    const withRules = base
      .filter((f) => !fieldRules || fieldRules[f.key]?.visible !== false)
      .map((f) => {
        let updated = f;
        if (fieldRules?.[f.key]) {
          const rule = fieldRules[f.key];
          const editableOn = rule.editable
            ? [...new Set([...f.editableOn, state.docState])]
            : f.editableOn.filter((s) => s !== state.docState && s !== '*');
          updated = { ...updated, editableOn };
        }
        if (dynamicOptions[f.key]) {
          updated = { ...updated, options: dynamicOptions[f.key] };
        }
        return updated;
      });
    return applyPermissions(withRules, userPermissions);
  }, [template.fields, fieldRules, dynamicOptions, state.docState, state.headerValues, userPermissions]);

  const lineFields = useMemo(() => {
    const base = template.fields.filter((f) => {
      if (f.context !== 'line' && f.context !== 'line-detail') return false;
      return isLineFieldVisibleInState(f, state.docState);
    });
    const withRules = base
      .filter((f) => !fieldRules || fieldRules[f.key]?.visible !== false)
      .map((f) => {
        let updated = f;
        if (fieldRules?.[f.key]) {
          const rule = fieldRules[f.key];
          const editableOn = rule.editable
            ? [...new Set([...f.editableOn, state.docState])]
            : f.editableOn.filter((s) => s !== state.docState && s !== '*');
          updated = { ...updated, editableOn };
        }
        if (dynamicOptions[f.key]) {
          updated = { ...updated, options: dynamicOptions[f.key] };
        }
        return updated;
      });
    return applyPermissions(withRules, userPermissions);
  }, [template.fields, fieldRules, dynamicOptions, state.docState, userPermissions]);

  // ── Resolved header sections (H1) ────────────────
  // Filters by docState (visibleOn), field conditions (visibleWhen/hiddenWhen) and RBAC.
  const resolvedHeaderSections = useMemo(() => {
    return template.layout.header.sections.filter((s) => {
      if (s.visibleOn && s.visibleOn.length > 0 && !s.visibleOn.includes('*')) {
        if (!s.visibleOn.includes(state.docState)) return false;
      }
      if (s.visibleWhen && !evaluateCondition(s.visibleWhen, state.headerValues as Record<string, unknown>)) return false;
      if (s.hiddenWhen && evaluateCondition(s.hiddenWhen, state.headerValues as Record<string, unknown>)) return false;
      if (s.permissionVisibility && userPermissions !== null && !userPermissions.includes(s.permissionVisibility)) return false;
      return true;
    });
  }, [template.layout.header.sections, state.docState, state.headerValues, userPermissions]);

  // ── Resolved line columns (H2) ───────────────────
  // Filters by field conditions (visibleWhen/hiddenWhen) and RBAC.
  const resolvedLineColumns = useMemo(() => {
    return template.layout.detail.columns.filter((c) => {
      if (c.visibleWhen && !evaluateCondition(c.visibleWhen, state.headerValues as Record<string, unknown>)) return false;
      if (c.hiddenWhen && evaluateCondition(c.hiddenWhen, state.headerValues as Record<string, unknown>)) return false;
      if (c.permissionVisibility && userPermissions !== null && !userPermissions.includes(c.permissionVisibility)) return false;
      return true;
    });
  }, [template.layout.detail.columns, state.headerValues, userPermissions]);

  // ── Detail tabs (multi-listados de líneas) ───────────
  //
  // Traduce `template.layout.detail.tabs` (declarativo) a `LineTabConfig[]` (ya
  // resuelto), que es lo único que ve el nivel presentacional. Aquí se aplica
  // todo lo que `MasterDocument` no puede saber: permisos, condiciones sobre el
  // encabezado, catálogo i18n, registro de iconos y registro de renderers.
  //
  // Ausente ⇒ `undefined`, no `[]`: es lo que le dice a `MasterDocument` que pinte
  // la grilla directamente, sin barra ni panel.
  const lineTabs = useMemo<LineTabConfig[] | undefined>(() => {
    const declared = template.layout.detail.tabs;
    if (!declared || declared.length === 0) return undefined;

    const headerValues = state.headerValues as Record<string, unknown>;

    const visible = declared.filter((tab) => {
      const type = tab.type ?? 'lines';
      // La pestaña de líneas no se filtra: es el único tipo con CRUD y el
      // documento no puede quedarse sin sus líneas por una condición mal puesta.
      if (type === 'lines') return true;
      if (tab.visibleWhen && !evaluateCondition(tab.visibleWhen, headerValues)) return false;
      if (tab.hiddenWhen && evaluateCondition(tab.hiddenWhen, headerValues)) return false;
      if (
        tab.permissionVisibility &&
        userPermissions !== null &&
        !userPermissions.includes(tab.permissionVisibility)
      ) {
        return false;
      }
      return true;
    });

    if (visible.length === 0) return undefined;

    return visible.map((tab) => {
      const type = tab.type ?? 'lines';
      // `labelKey` gana solo si el catálogo la resuelve; si el motor devuelve la
      // propia clave, se cae a `label` en vez de mostrar `master_document.…`.
      const translated = tab.labelKey ? t(tab.labelKey) : undefined;
      const label =
        (translated && translated !== tab.labelKey ? translated : undefined) ??
        tab.label ??
        (type === 'lines' ? t('master_document.lines.tab') : tab.key);

      const base: LineTabConfig = {
        key: tab.key,
        label,
        icon: resolveIcon(tab.icon),
        type,
      };

      if (type === 'lines') return base;

      const renderKey = (tab as { renderKey: string }).renderKey;
      const renderer = lineTabRenderers[renderKey];
      if (!renderer) {
        // Error de cableado del host: el template pide un renderer que nadie
        // registró. Se avisa aquí, que es donde se conoce el registro; la pestaña
        // queda con el estado vacío explícito de `LineTabsRegion`.
        console.warn(
          `[MasterDocument] la pestaña "${tab.key}" declara renderKey "${renderKey}" ` +
            'y no está registrado en MasterDocumentProvider.lineTabRenderers',
        );
        return base;
      }

      const flexQuery = type === 'flexQuery' ? (tab as DetailTabFlexQuery).flexQuery : undefined;
      const allowedActions =
        type === 'flexQuery' ? (tab as DetailTabFlexQuery).rowActions : undefined;

      // Filtros externos resueltos contra el documento, con la forma que espera
      // la prop `externalFilters` de <FlexQuery>. Un filtro cuyo valor no existe
      // NO viaja: un filtro `undefined` no es un filtro, y dejarlo pasar haría
      // que la consulta trajera todo el universo en vez de las líneas de este
      // documento. `isNull`/`isNotNull` son la excepción: no necesitan valor.
      const externalFilters = flexQuery?.externalFilters
        ?.map((filter): LineTabExternalFilter | null => {
          const op = filter.op ?? 'eq';
          if (op === 'isNull' || op === 'isNotNull') {
            return { column: filter.column, op, value: null };
          }
          const value = filter.headerField
            ? filter.headerField === '_documentId'
              ? state.documentId
              : headerValues[filter.headerField]
            : filter.value;
          if (value === undefined || value === null) return null;
          return { column: filter.column, op, value };
        })
        .filter((filter): filter is LineTabExternalFilter => filter !== null);

      const rowActions = (row: Record<string, unknown>): RowAction[] => {
        if (allowedActions === false) return [];
        const actions = getLineActions(row as DocumentLineData);
        if (Array.isArray(allowedActions)) {
          return actions.filter((action) => allowedActions.includes(action.key));
        }
        return actions;
      };

      return {
        ...base,
        // `helpers` los aporta `MasterDocument`, que es dueño del modal de línea:
        // el contexto se completa en el momento del render, no aquí.
        render: (helpers) =>
          renderer({
            openLineDetail: helpers.openLineDetail,
            rowClick: tab.type === 'flexQuery' ? ((tab as DetailTabFlexQuery).rowClick ?? 'none') : undefined,
            tabKey: tab.key,
            // Documento nuevo aún sin guardar ⇒ cadena vacía, que es la
            // convención del contrato (`DocumentState.documentId`).
            documentId: state.documentId ?? '',
            documentClass: state.documentClass ?? template.identity.documentClass,
            docState: state.docState,
            mode: state.mode,
            headerValues: state.headerValues,
            locale,
            flexQuery: flexQuery
              ? {
                  reportCode: flexQuery.reportCode,
                  contractBaseUrl: flexQuery.contractBaseUrl,
                  labelNamespace: flexQuery.labelNamespace,
                  view: flexQuery.view,
                  params: flexQuery.params,
                }
              : undefined,
            externalFilters,
            columns: resolvedLineColumns,
            rowActions,
            refreshToken,
            busy: state.saving,
            formatCurrency: adapter.formatCurrency,
            formatDate: adapter.formatDate?.bind(adapter),
            formatNumber: adapter.formatNumber?.bind(adapter),
          }),
      };
    });
  }, [
    template.layout.detail.tabs,
    template.identity.documentClass,
    state.headerValues,
    state.documentId,
    state.documentClass,
    state.docState,
    state.mode,
    state.saving,
    userPermissions,
    lineTabRenderers,
    resolvedLineColumns,
    getLineActions,
    refreshToken,
    locale,
    resolveIcon,
    adapter,
    t,
  ]);

  // ── canAddLine — encabezado lleno (§13.3.1) ─────────
  // Bloquea agregar líneas hasta que todos los campos required del encabezado tengan valor.
  // Además, si el template declara `layout.detail.addLineLockedWhen` y esa condición se cumple
  // contra `headerValues`, el botón queda deshabilitado (p.ej. descuento global activo).
  const canAddLine = useMemo(() => {
    if (state.mode === 'view') return false;
    const requiredFilled = headerFields
      .filter((f) => f.required)
      .every((f) => {
        const val = state.headerValues[f.key];
        return val !== null && val !== undefined && val !== '';
      });
    if (!requiredFilled) return false;
    const addLineLockedWhen = template.layout.detail.addLineLockedWhen;
    if (
      addLineLockedWhen &&
      evaluateCondition(addLineLockedWhen, state.headerValues as Record<string, unknown>)
    ) {
      return false;
    }
    return true;
  }, [state.mode, headerFields, state.headerValues, template.layout.detail.addLineLockedWhen]);

  // ── LookupField configs from template.searchers ──
  const lookupConfigs = useMemo(() => {
    const configs: Record<string, LookupFieldConfig> = {};
    for (const [fieldKey, cfg] of Object.entries(template.searchers ?? {})) {
      configs[fieldKey] = {
        entity: cfg.entity,
        displayFields: cfg.displayFields,
        searchFields: cfg.searchFields,
        displayTemplate: cfg.displayTemplate,
        valueField: cfg.valueField,
        pageSize: cfg.pageSize,
        filters: cfg.filters,
        bindFields: cfg.bindFields,
        filterDependencies: cfg.filterDependencies,
      };
    }
    return configs;
  }, [template.searchers]);

  // ── Handle LookupField record selection (bindFields) ──
  const handleRecordSelect = useCallback(
    (fieldKey: string, record: Record<string, unknown> | null) => {
      if (!record) return;
      const searcherCfg = template.searchers?.[fieldKey];
      if (!searcherCfg?.bindFields) return;
      // Apply bindFields: { "item_name": "nombre" } → set headerValues.item_name = record.nombre
      for (const [targetField, sourceField] of Object.entries(searcherCfg.bindFields)) {
        const value = record[sourceField];
        if (value !== undefined) {
          handleHeaderFieldChange(targetField, value);
        }
      }
    },
    [template.searchers, handleHeaderFieldChange],
  );

  // ── Server rules callback ────────────────────────
  const handleEvaluateServerRule = useCallback(
    (ruleId: string, formData: Record<string, unknown>) => {
      if (!adapter.evaluateServerRule) return Promise.resolve({});
      return adapter.evaluateServerRule(ruleId, formData, state.documentId);
    },
    [adapter, state.documentId],
  );

  // ── Widget slot (G4) ─────────────────────────────
  // Tracks the current form values from the line modal; fed by onLineFormValuesChange.
  const [lineFormValues, setLineFormValues] = useState<Record<string, unknown>>({});

  const lineWidgetSlot = useMemo<ReactNode>(() => {
    const widgets = template.layout.detail.lineModal?.widgets;
    if (!widgets || widgets.length === 0 || !adapter.getLineWidgetData) return undefined;

    // Filter widgets by visibleWhen/hiddenWhen conditions evaluated against current form values
    const visibleWidgets = widgets.filter((w: LineModalWidgetConfig) => {
      if (w.visibleWhen && !evaluateCondition(w.visibleWhen as FieldCondition, lineFormValues)) return false;
      if (w.hiddenWhen && evaluateCondition(w.hiddenWhen as FieldCondition, lineFormValues)) return false;
      return true;
    });

    if (visibleWidgets.length === 0) return undefined;

    return (
      <div className="flex flex-col h-full divide-y divide-border-secondary dark:divide-dark-border-primary">
        {visibleWidgets.map((w: LineModalWidgetConfig) => (
          <LineWidgetPanel
            key={w.widgetKey}
            config={w}
            formValues={lineFormValues}
            documentId={state.documentId}
            adapter={adapter}
          />
        ))}
      </div>
    );
  }, [template.layout.detail.lineModal?.widgets, adapter, lineFormValues, state.documentId]);

  // ── Navegación a otro documento existente ─────────
  // Se delega en el host si pasó el callback; si no, se emite el pseudo-key histórico
  // `navigate_document:<id>`, que MasterDocumentPage intercepta en su adapter. Los dos
  // orígenes (trazabilidad y generación por cruce) entran por aquí para que el host
  // resuelva el destino en un solo lugar.
  const navigateToDocument = useCallback(
    (targetDocumentId: string, reason: DocumentNavigationReason) => {
      if (onNavigateToDocument) {
        onNavigateToDocument(targetDocumentId, { reason });
        return;
      }
      void handleAction(`navigate_document:${targetDocumentId}`);
    },
    [onNavigateToDocument, handleAction],
  );

  // ── Crossing ──────────────────────────────────────
  const baseUrl = (adapter as { baseUrl?: string }).baseUrl ?? 'http://localhost:5204';
  const crossingFetch = useMemo(() => createCrossingFetch(adapter, baseUrl), [adapter, baseUrl]);

  const crossing = useCrossing({
    config: template.crossing,
    documentId: state.documentId,
    docState: state.docState,
    hasLines: state.lines.length > 0,
    crossingFetch,
    onDocumentGenerated: (docId) => navigateToDocument(docId, 'generated'),
  });

  const crossingProps = useMemo(() => {
    if (!crossing || !template.crossing) return undefined;
    const { state: cs, actions: ca } = crossing;
    return {
      sourceSelector: {
        selectedDocuments: cs.selectedSourceDocuments,
        onAdd: ca.addSourceDocument,
        onRemove: ca.removeSourceDocument,
        locked: cs.sourceSelectorLocked,
        searchDocuments: ca.searchSourceDocuments,
      },
      selectionMode: template.crossing.generateOn.includes(state.docState),
      selectedLineIds: cs.selectedLineIds,
      onLineSelect: ca.toggleLineSelection,
      onTrySelectTerminalLine: ca.trySelectTerminalLine,
      lineStatuses: cs.lineStatuses,
      lineSelectionErrors: cs.lineSelectionErrors,
      pendingLines: cs.pendingLines,
      quantitiesToCross: cs.quantitiesToCross,
      onQuantityChange: ca.setQuantityToCross,
      formatNumber: adapter.formatNumber,
      pendingLinesFetchedAt: cs.pendingLinesFetchedAt,
      pendingLinesLoading: cs.pendingLinesLoading,
      onRefreshPendingLines: ca.refreshPendingLines,
      activeTab: cs.activeTab,
      onTabChange: ca.setActiveTab,
      traceabilityLabel: template.crossing.traceability?.label ?? t('master_document.document.traceability'),
      traceabilityTree: cs.traceabilityTree,
      traceabilityLoading: cs.traceabilityLoading,
      traceabilityError: cs.traceabilityError,
      onRetryTraceability: ca.refreshTraceability,
      onTraceabilityNodeClick: (nodeId: string) => navigateToDocument(nodeId, 'traceability'),
      classMenuOptions: cs.crossingClassOptions,
      // selectClass cierra el menú y abre el modal de header con la clase elegida.
      onSelectClass: ca.selectClass,
      canGenerate: cs.canGenerate,
      lineCloseModal: cs.lineCloseModal,
      onOpenLineCloseModal: ca.openLineCloseModal,
      onCloseLineCloseModal: ca.closeLineCloseModal,
      onConfirmLineClose: ca.confirmLineClose,
      lineActions: template.crossing?.lineActions,
      generateModal: cs.generateModal,
      onOpenGenerateModal: ca.openGenerateModal,
      onCancelGenerateModal: ca.cancelGenerateModal,
      onConfirmGenerate: ca.confirmGenerate,
      generateError: cs.generateError,
      onClearGenerateError: ca.clearGenerateError,
      generateHeaderFields: template.crossing?.generateHeaderFields ?? [],
      pageErrors: cs.pageErrors,
      onDismissPageError: ca.dismissPageError,
    };
  }, [crossing, template.crossing, state.docState, state.lines.length, navigateToDocument, t]);

  // ── Render ────────────────────────────────────────
  return (
    <MasterDocumentRefreshProvider value={refreshApi}>
      <MasterDocument
        documentId={state.documentId}
        documentNumber={(state.headerValues['_documentNumber'] as string) ?? undefined}
        docTypeBadge={template.identity.label}
        breadcrumbLabel={template.identity.breadcrumbLabel}
        documentTitle={template.identity.label}
        companyName={companyName}
        onBreadcrumbClick={() => handleAction('navigate_back')}
        docState={state.docState}
        stateBadge={resolvedStateBadge}
        mode={state.mode}
        transitions={transitions}
        onTransition={runTransition}
        actions={actionsWithAudit}
        contextItems={contextItems}
        currencyPanel={currencyPanel}
        headerLayout={{ ...template.layout.header, sections: resolvedHeaderSections }}
        headerFields={headerFields}
        headerValues={state.headerValues}
        headerErrors={state.headerErrors}
        onHeaderFieldChange={handleHeaderFieldChange}
        dynamicEntitiesSlot={
          dynamicEntities.header ? (
            <DocumentDynamicEntities
              grain={dynamicEntities.header}
              // Vacío en creación: el bloque se captura igual y los valores se
              // persisten cuando el POST del documento devuelve su id.
              recordId={state.documentId || undefined}
              defaultTitleKey="master_document.dynamicEntities.headerTitle"
              readOnly={state.mode === 'view'}
            />
          ) : undefined
        }
        lineDynamicEntitiesSlot={
          dynamicEntities.line
            ? ({ lineId }) => (
                <DocumentDynamicEntities
                  // Remontar al cambiar de línea es intencional: los valores de
                  // una entidad viven dentro de su formulario, y reutilizar la
                  // instancia arrastraría lo capturado en la línea anterior.
                  key={lineId ?? '__new__'}
                  grain={dynamicEntities.line!}
                  recordId={lineId ?? undefined}
                  defaultTitleKey="master_document.dynamicEntities.lineTitle"
                  readOnly={state.mode === 'view'}
                  // Dentro del modal de línea el marco ya lo pone el modal: una
                  // tarjeta aquí sería una caja dentro de otra caja.
                  variant="bare"
                />
              )
            : undefined
        }
        lookupConfigs={lookupConfigs}
        fetcher={adapter.fetcher}
        onRecordSelect={handleRecordSelect}
        linesTitle={template.layout.detail.title}
        lines={state.lines}
        lineColumns={resolvedLineColumns}
        lineFields={lineFields}
        lineComputedRules={lineComputedRules}
        lineServerRules={template.serverRules}
        onEvaluateServerRule={adapter.evaluateServerRule ? handleEvaluateServerRule : undefined}
        lineModalSections={template.layout.detail.lineModal?.sections}
        serialSupport={template.layout.detail.serialSupport}
        serialReadOnly={
          state.mode === 'view' ||
          (template.layout.detail.serialSupport
            ? state.docState !== template.stateMachine.initial
            : undefined)
        }
        validateSerial={adapter.validateSerial?.bind(adapter)}
        fetchAvailableSerials={adapter.fetchAvailableSerials?.bind(adapter)}
        lineWidgetSlot={lineWidgetSlot}
        onLineFormValuesChange={adapter.getLineWidgetData ? setLineFormValues : undefined}
        canAddLine={canAddLine && operacionDeLineaPermitida(
          undefined,
          template.layout.detail.lineModal?.addableOn,
          state.docState,
        )}
        onAddLine={() => {}}
        onEditLine={() => {}}
        onDeleteLine={runDeleteLine}
        onDuplicateLine={state.mode !== 'view' ? runDuplicateLine : undefined}
        getLineActions={template.lineActionRegistry ? getLineActions : undefined}
        lineTabs={lineTabs}
        // Se derivan del template y de SU PROPIO `docState`, no se piden al host:
        // `SmartMasterDocumentProps` no expone callbacks ni estado de línea, así que
        // sin esta derivación una página que monta este componente no tendría forma
        // de declarar nada. `operacionDeLineaPermitida` cruza el booleano con la
        // lista de estados; ausentes ambos, el comportamiento no cambia.
        lineEditable={operacionDeLineaPermitida(
          template.layout.detail.lineModal?.editable,
          template.layout.detail.lineModal?.editableOn,
          state.docState,
        )}
        lineDeletable={operacionDeLineaPermitida(
          template.layout.detail.lineModal?.deletable,
          template.layout.detail.lineModal?.deletableOn,
          state.docState,
        )}
        onLineSave={runLineSave}
        factBoxSections={factBoxSections}
        conflict={conflictProp}
        forbidden={forbiddenProp}
        crossing={crossingProps}
        loading={state.loading}
        error={state.error}
        busy={state.saving}
        busyLineId={busyLineId}
      />

      <CurrencySelectorModal
        open={currencyModalOpen}
        currentCode={currencyCode}
        currencies={availableCurrencies}
        loading={currenciesLoading}
        onSelect={handleCurrencySelect}
        onClose={() => setCurrencyModalOpen(false)}
      />

      {activeModalListConfig && adapter.genericFetch && (
        <DocumentModalList
          open={modalListOpen}
          onClose={() => setModalListOpen(false)}
          config={activeModalListConfig}
          documentId={state.documentId}
          lineId={modalListLineId}
          fetcher={adapter.genericFetch.bind(adapter)}
          formatCurrency={adapter.formatCurrency}
          formatDate={adapter.formatDate}
        />
      )}

      {template.createFromSource && (
        <CreateFromSourceModal
          open={createFromSourceOpen}
          mode="append"
          config={template.createFromSource}
          adapter={adapter}
          documentId={state.documentId}
          operationCenterId={String(state.headerValues['operationCenterId'] ?? state.headerValues['operation_center_id'] ?? '') || undefined}
          lockedThirdPartyId={
            template.createFromSource.thirdPartyHeaderField
              ? String(state.headerValues[template.createFromSource.thirdPartyHeaderField] ?? '') || undefined
              : undefined
          }
          userPermissions={userPermissions}
          formatCurrency={adapter.formatCurrency}
          formatNumber={adapter.formatNumber}
          onClose={() => setCreateFromSourceOpen(false)}
          onAppended={() => {
            setCreateFromSourceOpen(false);
            void refetchDocument();
          }}
        />
      )}

      {activeLineAction && activeLineAction.entry.formModal && (
        <LineActionFormModal
          open
          onClose={() => {
            if (lineActionBusy) return;
            setActiveLineAction(null);
            setLineActionLineId('');
            setLineActionError(undefined);
          }}
          title={activeLineAction.entry.formModal.title}
          submitLabel={activeLineAction.entry.formModal.submitLabel}
          fields={activeLineAction.entry.formModal.fields}
          busy={lineActionBusy}
          error={lineActionError}
          onSubmit={(formData) => handleLineAction(activeLineAction.key, lineActionLineId, formData, activeLineAction.entry)}
        />
      )}

      {pendingDeleteLineId !== null && (
        <ConfirmDialog
          message={t('master_document.lines.deleteConfirm')}
          onConfirm={confirmDeleteLine}
          onCancel={() => setPendingDeleteLineId(null)}
        />
      )}
    </MasterDocumentRefreshProvider>
  );
}
